
import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, File, Clock, Calendar, Eye, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

interface Recording {
  id: string;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  downloadUrl?: string;
}

interface StreamRecordingDownloadProps {
  streamId: number;
  onDownload?: (recordingId: string) => void;
}

export function StreamRecordingDownload({ streamId, onDownload }: StreamRecordingDownloadProps) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const fetchRecordings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ recordings: Recording[] }>(`api/streams/${streamId}/recordings`);

      if (response.success && response.data) {
        setRecordings(response.data.recordings || []);
      } else {
        toast.error(response.error?.message || 'Failed to load recordings');
        console.error('Fetch recordings error:', response.error);
      }
    } catch (error) {
      toast.error('Failed to load recordings');
      console.error('Fetch recordings error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadRecording = async (recording: Recording) => {
    if (!recording.downloadUrl || recording.status !== 'ready') {
      toast.error('Recording is not ready for download');
      return;
    }

    setIsDownloading(recording.id);
    try {
      // For security, we'll open the download URL in a new tab/window
      // In a real implementation, you might want to handle this differently
      window.open(recording.downloadUrl, '_blank');
      
      toast.success('Download started');
      onDownload?.(recording.id);
    } catch (error) {
      toast.error('Failed to start download');
      console.error('Download recording error:', error);
    } finally {
      setIsDownloading(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchRecordings();
    
  }, [streamId]);

  const readyRecordings = recordings.filter(r => r.status === 'ready');
  const processingRecordings = recordings.filter(r => r.status === 'processing');
  const failedRecordings = recordings.filter(r => r.status === 'failed');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Download Recordings
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Download available recordings for this stream
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={fetchRecordings} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Ready for Download */}
            {readyRecordings.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Ready for Download ({readyRecordings.length})
                </h4>
                <div className="space-y-2">
                  {readyRecordings.map((recording) => (
                    <div key={recording.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <File className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{recording.title}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDuration(recording.duration)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(recording.createdAt).toLocaleDateString()}
                            </div>
                            <span>{formatFileSize(recording.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => downloadRecording(recording)}
                        disabled={isDownloading === recording.id}
                        className="flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        {isDownloading === recording.id ? 'Downloading...' : 'Download'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Processing */}
            {processingRecordings.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-yellow-500" />
                  Processing ({processingRecordings.length})
                </h4>
                <div className="space-y-2">
                  {processingRecordings.map((recording) => (
                    <div key={recording.id} className="flex items-center justify-between p-3 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                      <div className="flex items-center gap-3">
                        <File className="h-5 w-5 text-yellow-500" />
                        <div>
                          <p className="font-medium">{recording.title}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDuration(recording.duration)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(recording.createdAt).toLocaleDateString()}
                            </div>
                            <span>{formatFileSize(recording.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="secondary" className="animate-pulse">
                        Processing
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed */}
            {failedRecordings.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Failed ({failedRecordings.length})
                </h4>
                <div className="space-y-2">
                  {failedRecordings.map((recording) => (
                    <div key={recording.id} className="flex items-center justify-between p-3 border rounded-lg bg-red-50 dark:bg-red-900/20">
                      <div className="flex items-center gap-3">
                        <File className="h-5 w-5 text-red-500" />
                        <div>
                          <p className="font-medium">{recording.title}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDuration(recording.duration)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(recording.createdAt).toLocaleDateString()}
                            </div>
                            <span>{formatFileSize(recording.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                      <Badge variant="destructive">Failed</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Recordings */}
            {recordings.length === 0 && (
              <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recordings available</p>
                <p className="text-sm mt-2">Recordings will appear here when available</p>
              </div>
            )}
          </>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Total recordings: {recordings.length}
        </div>
        <div className="flex gap-2">
          {readyRecordings.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const firstReady = readyRecordings[0];
                if (firstReady) downloadRecording(firstReady);
              }}
              disabled={isDownloading !== null || readyRecordings.length === 0}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview First
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={fetchRecordings}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}